// Helper de envios — espejo de la matriz que vive en la funcion SQL `calcular_envio`.
//
// Se usa para mostrar PREVIEW en el frontend sin tener que pegarle a la DB en cada
// teclazo (ej: el carrito de la tienda recalcula envio cuando agregas/sacas items).
//
// La fuente de verdad sigue siendo el backend: cuando se crea el pedido, se usa la
// RPC `calcular_envio_para_usuario` y se valida ahi. Este modulo es solo para UI
// reactiva.

import { supabase } from './supabase'

// ─── Tarifas ───────────────────────────────────────────────
// Si cambian en `envio_tarifas` (DB), tambien hay que actualizarlas aca o cargarlas
// con `cargarTarifasDesdeDB()` al iniciar la app.
let TARIFAS = {
  suscriptor_fuera_ruta:           165,
  no_susc_kit_fuera_ruta:          165,
  no_susc_tienda_chica_en_ruta:    165,
  no_susc_tienda_chica_fuera_ruta: 250,
  no_susc_tienda_grande_fuera_ruta:165,
  umbral_tienda_envio_gratis:     1000,
}

/** Trae las tarifas vigentes de la DB y actualiza el cache local. */
export async function cargarTarifasDesdeDB() {
  const { data, error } = await supabase.from('envio_tarifas').select('clave, monto')
  if (error || !data) return TARIFAS
  data.forEach(t => { TARIFAS[t.clave] = Number(t.monto) })
  return { ...TARIFAS }
}

export function getTarifas() { return { ...TARIFAS } }

// ─── Mapeo Date → dia_semana del backend ──────────────────
const DIAS = ['domingo','lunes','martes','miercoles','jueves','viernes','sabado']
export function diaSemanaDe(fecha) {
  const d = (fecha instanceof Date) ? fecha : new Date(fecha + 'T12:00:00')
  return DIAS[d.getDay()]
}

/**
 * Devuelve el estado del barrio para una fecha dada.
 * @param {{ activo:boolean, dias:string[] }} barrio
 * @param {Date|string} fecha
 * @returns {'en_ruta' | 'fuera_de_ruta' | 'fuera_de_cobertura'}
 */
export function estadoBarrio(barrio, fecha) {
  // Asumimos que si se pasa un barrio, ya viene de getBarriosActivos (ya filtrado por activo=true).
  // No chequeamos `barrio.activo` porque ese campo puede no estar en el SELECT.
  if (!barrio || !Array.isArray(barrio.dias)) return 'fuera_de_cobertura'
  const dia = diaSemanaDe(fecha)
  return barrio.dias.includes(dia) ? 'en_ruta' : 'fuera_de_ruta'
}

/**
 * Calcula el costo de envio en el cliente (espejo de la funcion SQL).
 *
 * @param {Object} args
 * @param {boolean} args.esSuscriptor
 * @param {'compra_unica'|'productos_sueltos'|'adicional_suscripcion'|'suscripcion'} args.tipo
 * @param {{activo:boolean, dias:string[]}} args.barrio
 * @param {Date|string} args.fecha
 * @param {number} [args.monto=0]   monto del subtotal (sin envio)
 * @param {boolean} [args.fueraDeCorte=false]  si true, se cobra como "fuera de ruta"
 *                                             (cliente acepta saltar el corte pagando)
 *
 * @returns {{
 *   permitido: boolean,
 *   costoEnvio: number,
 *   estadoBarrio: 'en_ruta'|'fuera_de_ruta'|'fuera_de_cobertura',
 *   enRuta: boolean,
 *   fueraDeCorte: boolean,
 *   motivo: string | null,
 * }}
 */
export function calcularEnvio({ esSuscriptor, tipo, barrio, fecha, monto = 0, fueraDeCorte = false }) {
  const estado = estadoBarrio(barrio, fecha)

  if (estado === 'fuera_de_cobertura') {
    return {
      permitido: false,
      costoEnvio: 0,
      estadoBarrio: estado,
      enRuta: false,
      fueraDeCorte: false,
      motivo: 'Por ahora no llegamos a esa zona',
    }
  }

  const enRuta = estado === 'en_ruta'
  // "aplica recargo" si esta fuera de ruta normal O si esta en ruta pero saltando el corte
  const aplicaRecargo = !enRuta || fueraDeCorte
  let costo = 0

  if (esSuscriptor) {
    costo = aplicaRecargo ? TARIFAS.suscriptor_fuera_ruta : 0
  } else {
    if (tipo === 'compra_unica') {
      costo = aplicaRecargo ? TARIFAS.no_susc_kit_fuera_ruta : 0
    } else if (tipo === 'productos_sueltos') {
      const grande = monto >= TARIFAS.umbral_tienda_envio_gratis
      if (grande) {
        costo = aplicaRecargo ? TARIFAS.no_susc_tienda_grande_fuera_ruta : 0
      } else {
        costo = aplicaRecargo ? TARIFAS.no_susc_tienda_chica_fuera_ruta : TARIFAS.no_susc_tienda_chica_en_ruta
      }
    } else {
      return {
        permitido: false,
        costoEnvio: 0,
        estadoBarrio: estado,
        enRuta,
        fueraDeCorte,
        motivo: 'Tipo de pedido invalido para no suscriptor',
      }
    }
  }

  return {
    permitido: true,
    costoEnvio: costo,
    estadoBarrio: estado,
    enRuta,
    fueraDeCorte,
    motivo: null,
  }
}

/**
 * Llama a la funcion SQL canonica (la fuente de verdad) — usar antes de crear pedido.
 */
export async function calcularEnvioServer({ tipo, barrioId, fecha, monto = 0, fueraDeCorte = false }) {
  const fechaStr = (fecha instanceof Date) ? fecha.toISOString().slice(0,10) : fecha
  const { data, error } = await supabase.rpc('calcular_envio_para_usuario', {
    p_tipo: tipo,
    p_barrio_id: barrioId,
    p_fecha: fechaStr,
    p_monto: Number(monto || 0),
    p_fuera_de_corte: !!fueraDeCorte,
  })
  if (error) throw error
  const r = data?.[0]
  if (!r) throw new Error('Sin respuesta de calcular_envio')
  return {
    permitido: r.permitido,
    costoEnvio: Number(r.costo_envio),
    estadoBarrio: r.estado_barrio,
    enRuta: r.en_ruta,
    fueraDeCorte: r.fuera_de_corte,
    motivo: r.motivo,
    esSuscriptor: r.es_suscriptor,
  }
}

/**
 * Devuelve las proximas N opciones de fecha de entrega para un barrio,
 * marcando cuales estan fuera de corte (snap a viernes incluido).
 *
 * @param {Object} args
 * @param {string} args.barrioId
 * @param {Date}   [args.desde=new Date()]
 * @param {number} [args.maxOpciones=4]
 * @param {number} [args.diasAntes=2]            cuanto antes es el corte (default D-2)
 * @param {number} [args.margenMinHoras=24]      margen absoluto entre compra y entrega
 *
 * @returns {Promise<Array<{
 *   fechaEntrega: string,        // 'YYYY-MM-DD'
 *   nombreDia: string,           // 'lunes' | 'martes' | ...
 *   fechaCorte: string,          // ISO timestamp
 *   fueraDeCorte: boolean,       // si ya paso el corte → cliente paga recargo
 * }>>}
 */
export async function proximasFechasEntrega({
  barrioId,
  desde = new Date(),
  maxOpciones = 6,
  diasAntes = 2,
  margenMinHoras = 24,
  soloEnRuta = false,
} = {}) {
  const { data, error } = await supabase.rpc('proximas_fechas_entrega', {
    p_barrio_id: barrioId,
    p_desde: desde.toISOString(),
    p_max_opciones: maxOpciones,
    p_dias_antes: diasAntes,
    p_margen_min_horas: margenMinHoras,
    p_solo_en_ruta: soloEnRuta,
  })
  if (error) throw error
  return (data || []).map(r => ({
    fechaEntrega: r.fecha_entrega,
    nombreDia: r.nombre_dia,
    fechaCorte: r.fecha_corte,
    fueraDeCorte: r.fuera_de_corte,
    enRuta: r.en_ruta,
  }))
}

// ─── Tip para incentivar carrito mas grande ────────────────
/**
 * Devuelve mensaje sugerido cuando NO suscriptor + tienda < umbral, en ruta.
 * Le dice "agregá $X mas y el envio sale gratis".
 */
export function tipParaUpsellTienda({ esSuscriptor, tipo, monto, enRuta }) {
  if (esSuscriptor) return null
  if (tipo !== 'productos_sueltos') return null
  if (!enRuta) return null
  const umbral = TARIFAS.umbral_tienda_envio_gratis
  if (monto >= umbral) return null
  const falta = umbral - monto
  return {
    falta,
    texto: `Agregá $${falta.toLocaleString('es-UY')} más para que el envío te salga gratis.`,
  }
}

// ─── Calcular fecha_corte (D-2 antes de la entrega) ──────────
/**
 * Devuelve el momento limite para cancelar/modificar el pedido sin cargo.
 * Por defecto: 2 dias antes de la entrega a las 23:59:59 (hora local UY).
 */
export function calcularFechaCorte(fechaEntrega, diasAntes = 2) {
  const f = (fechaEntrega instanceof Date) ? new Date(fechaEntrega) : new Date(fechaEntrega + 'T23:59:59')
  f.setDate(f.getDate() - diasAntes)
  return f
}

// ─── Etiqueta visual del estado del barrio ────────────────────
export const ESTADO_BARRIO_LABEL = {
  en_ruta:            { texto: 'En ruta',           color: '#10b981', emoji: '🟢' },
  fuera_de_ruta:      { texto: 'Fuera de ruta',     color: '#f59e0b', emoji: '🟡' },
  fuera_de_cobertura: { texto: 'Sin cobertura',     color: '#ef4444', emoji: '🔴' },
}
